/**
 * 공개 사이트 공통 틀 문구 — ko/landingSite.ts 와 같은 키(정본은 ko).
 */
const landingSite = {
  "skipToContent": "Skip to content",
  "homeLabel": "DroneSquare home",
  "nav": {
    "label": "Main menu",
    "mobileLabel": "Mobile menu",
    "openMenu": "Open menu",
    "closeMenu": "Close menu",
    "services": "Services",
    "plans": "Plans",
    "inquiry": "Contact us",
    "news": "Press",
    "manual": "Manual",
    "releases": "What's new"
  },
  "servicePanel": {
    "platform": {
      "title": "Platform",
      "description": "2D·3D·4D inspection, sections and earthwork, site records and monitoring"
    },
    "survey": {
      "title": "Drone survey & spatial data processing",
      "description": "From flight approval to orthomosaics, DSM, 3D models and digital topographic maps"
    },
    "more": "Learn more →"
  },
  "account": {
    "login": "Login",
    "start": "Get started",
    "logout": "Logout",
    "flightHub": "Drone Control System",
    "flightHubFailed": "Unable to connect to the drone control system. Please try again later."
  },
  "share": {
    "label": "Share",
    "menuLabel": "Share this page",
    "copied": "Link copied",
    "failed": "Couldn't copy the link."
  },
  "footer": {
    "menuLabel": "Footer menu",
    "company": "About",
    "terms": "Terms of Service",
    "privacy": "Privacy Policy",
    "contact": "Partnership/Inquiry",
    "companyName": "ALTIVION Inc.",
    "businessNumber": "Business registration number: 146-81-03772",
    "address": "Room 707, 7F, KD U Tower, 640 Munjeong-dong, Songpa-gu, Seoul 05836, Republic of Korea",
    "telLabel": "Main phone",
    "hours": "Hours: 10:00 - 18:30 · Lunch: 12:00 - 13:30",
    "closed": "Closed on Saturdays, Sundays and public holidays",
    "copyright": "Copyright © ALTIVION Inc. All rights reserved."
  },
  "contact": {
    "telLabel": "Main phone",
    "hoursLines": ["Hours: 10:00 - 18:30", "Lunch: 12:00 - 13:30", "Closed on Saturdays, Sundays and public holidays"],
    "inquiry": "Contact us"
  },
  "sectionIndex": "Section navigation",
  "sectionIndexItem": "Section {{n}}",
  "flow": {
    "label": "Drone mapping process",
    "steps": [
      {
        "title": "Flight approval",
        "text": "We check the flight area and altitude, and obtain flight and filming approval under the relevant regulations.",
        "alt": "A finger tracing the airspace boundary on an aeronautical chart"
      },
      {
        "title": "Flight planning · autonomous flight",
        "text": "We set the flight path and photo overlap for the site extent and required resolution, then capture with autonomous flight.",
        "alt": "A camera drone flying in a blue sky"
      },
      {
        "title": "GCP survey",
        "text": "We install and survey ground control points (GCPs) to improve the positional accuracy of the results.",
        "alt": "A total station and prism on tripods"
      },
      {
        "title": "Image · video capture",
        "text": "We fly the planned route and capture photos and video of the site.",
        "alt": "A highway interchange photographed by drone"
      },
      {
        "title": "Orthomosaic · DSM · 3D model processing",
        "text": "We align the photos to produce a point cloud, orthomosaic, DSM and 3D model.",
        "alt": "A 3D model of an earthwork site built from drone photos, with cut slopes and flat ground in relief"
      },
      {
        "title": "Platform",
        "text": "We upload the orthomosaic and 3D model to the DroneSquare platform so you can view and measure them on the map.",
        "alt": "DroneSquare app mark"
      }
    ]
  },
  "education": {
    "eyebrow": "Training · consulting · regulations · safety",
    "title": "From drone operation to software,<br/>we work with you on site",
    "cards": [
      {
        "kicker": "Training",
        "title": "On-site training",
        "text": "Hands-on training from drone operation to software use helps your team apply it on the job.",
        "alt": "Two hands operating a drone controller with a tablet attached"
      },
      {
        "kicker": "Consulting",
        "title": "The best way to use drones",
        "text": "We provide drone strategies and execution plans tailored to your project.",
        "alt": "Two people in high-visibility vests pointing at drawings together"
      },
      {
        "kicker": "Regulations",
        "title": "Compliance with flight and survey regulations",
        "text": "We follow drone flight and survey regulations to deliver safe, lawful services.",
        "alt": "A worker's hand writing on a clipboard"
      },
      {
        "kicker": "Safety training",
        "title": "Guidelines for worker safety",
        "text": "We provide structured training and guidelines for worker safety when using drones.",
        "alt": "A hard hat, gloves, ear muffs and safety glasses"
      }
    ]
  },
  "partners": {
    "title": "Our partners",
    "logos": [
      "Soosung Engineering",
      "Taeyoung E&C",
      "Hyundai E&C",
      "SK ecoengineering",
      "Woodae E&P",
      "K water Tech",
      "Punglim Industrial",
      "Hwalrim Construction",
      "Korea Fisheries Infrastructure Public Agency",
      "HJ Shipbuilding & Construction",
      "Liberaware"
    ]
  },
  "rows": {
    "pfLayers": {
      "kicker": "Section layers",
      "title": "Pick capture dates and layers to overlay",
      "text": "In the section graph's layer list, turn ground lines by capture date, DXF design lines and BIM on and off to compare them in one graph.",
      "alt": "A real section graph and layer list, selecting mesh layers from three capture dates."
    },
    "earthwork": {
      "kicker": "Earthwork by alignment and zone",
      "title": "Draw a zone to get earthwork for every capture date",
      "text": "Cut and fill volumes are calculated automatically for every capture date. See progress against planned volumes and what remains in one table, and download it as CSV.",
      "alt": "The earthwork summary table on the real earthwork screen, splitting cut and fill by material with planned quantities, remaining volumes, cumulative totals and progress by capture date."
    },
    "measure": {
      "kicker": "2D/3D measurement",
      "title": "Length, area and volume right on screen",
      "text": "Click a point to get its coordinates and elevation; connect points to get each segment's length. Draw an area to see surface area and volume that account for slopes.",
      "alt": "An area measurement on a flat lot in the real 3D view (zoomed in). The center chip shows a 3D area of 1330.57 m² and a planar area of 1328.98 m², with each side's length."
    },
    "issue": {
      "kicker": "Issues",
      "title": "Leave issues where they are and check them by date",
      "text": "Mark issues as points, lines or areas and record their details and status. Switch capture dates to see how the same spot changes.",
      "alt": "A selected slope erosion issue area in the real 2D view, with the issue panel on the left showing capture dates, the severity trend and issue details."
    },
    "geolog": {
      "kicker": "Photos · videos",
      "title": "Site photos and videos gathered where they were taken",
      "text": "Upload photos and videos from the site and they gather as markers by location. Click a marker to open them right there.",
      "alt": "The photo and video panel on the left of the real 2D view and a video record card opened from a map marker."
    },
    "pano": {
      "kicker": "360°",
      "title": "Inside the site with 360° photos",
      "text": "Upload 360° photos and look around inside the site as if standing where they were taken.",
      "alt": "The full 360° view in the real app: the interior of a building under construction, a location mini map, photo paging (1/3) and a 2D switch button."
    },
    "equip": {
      "kicker": "Equipment route simulation",
      "title": "Equipment routes and overlapping working radii, in advance",
      "text": "Place equipment and draw routes in Site Monitoring 3D. During playback the equipment moves and danger marks appear where working radii overlap.",
      "alt": "In the real Site Monitoring 3D view, two excavators pass each other on opposite routes; a danger zone appears when their working radii overlap, and a dump truck crosses between them heading north."
    }
  },
  "scenes": {
    "view": {
      "eyebrow": "2D · 3D · 4D",
      "title": "From flat to 3D, and through time",
      "lead": "Check drone-captured sites as 2D orthomosaics and 3D models,<brd/> and split the screen into up to four views to compare capture dates.",
      "stepsLabel": "View steps",
      "steps": [
        {
          "k": "2D orthomosaic",
          "d": "A high-resolution map made from drone photos"
        },
        {
          "k": "3D model",
          "d": "Switch the same site to the 3D view"
        },
        {
          "k": "4D time series",
          "d": "Split the screen in four to compare capture dates"
        }
      ],
      "viewLabel": "An earthwork site built from real drone data. The orthomosaic map switches to the 3D view, then the screen splits in four to compare construction progress on four capture dates from March 2025 to March 2026."
    },
    "bridge": {
      "eyebrowLanding": "Survey · earthwork · land",
      "eyebrowPlatform": "Survey · sections · earthwork",
      "title": "Draw one line to see into the ground and structures",
      "lead": "Draw a line on the 3D view to get a section cut through terrain and BIM together.<brd/> Overlay capture dates and export to TXT, CSV or DXF right away.",
      "stepsLabel": "Section steps",
      "steps": [
        {
          "k": "Longitudinal section",
          "d": "Draw a line along the bridge axis to get a section"
        },
        {
          "k": "Cross section",
          "d": "A section drawn across a pier"
        },
        {
          "k": "Overlay capture dates",
          "d": "Ground lines by date plus DXF and BIM layers in one graph"
        },
        {
          "k": "Export",
          "d": "Set the point spacing and export to TXT, CSV or DXF"
        }
      ],
      "viewLabel": "A 3D model of a real bridge construction site overlaid with the bridge BIM (girders, slabs, piers, abutments). Drawing a line along the bridge axis plots the ground line and bridge section in the longitudinal graph below, followed by a cross section at a pier, a time-series comparison of two capture dates and point-spacing data export (TXT, CSV, DXF).",
      "lon": "Longitudinal",
      "crs": "Cross section",
      "capture": "Capture image",
      "extract": "Extract points",
      "popTitle": "Data export",
      "popSpacing": "Point spacing",
      "popUnit": "m",
      "dateNow": "2026.03.20",
      "datePast": "2025.11.21",
      "bim": "Bridge BIM",
      "distance": "Distance (m)",
      "height": "Elevation (m)"
    },
    "layers": {
      "eyebrow": "Layer overlay",
      "title": "All your data at once",
      "lead": "Overlay design drawings, cadastral data and site records in one 3D view,<brd/> and check risks to people and equipment in advance in Site Monitoring 3D.",
      "stepsLabel": "Layer steps",
      "steps": [
        {
          "k": "DXF · cadastral GIS",
          "d": "Planned alignment and parcel boundaries on the 3D site"
        },
        {
          "k": "BIM",
          "d": "Structure BIM together with the site terrain"
        },
        {
          "k": "Point Cloud",
          "d": "The site as raw point data"
        },
        {
          "k": "Vectors",
          "d": "Buried pipes as volume lines"
        },
        {
          "k": "Clipping",
          "d": "Cut with a box to see sections underground"
        },
        {
          "k": "Site records",
          "d": "Issues, photos, videos and 360° at each location"
        },
        {
          "k": "People · equipment simulation",
          "d": "Working radii and route risks in advance"
        }
      ],
      "viewLabel": "A 3D view of an earthwork site built from real drone data. Design alignment and cadastral boundaries, retaining wall BIM, point cloud, buried pipes (volume lines), a box clipping section, and issue and site photo locations are overlaid in turn; then in Site Monitoring 3D, excavators and a dump truck move along drawn routes and a danger zone appears when two excavators' working radii overlap.",
      "wallBim": "Retaining wall BIM",
      "waterPipe": "Water main",
      "stormPipe": "Storm drain",
      "issues": [
        "Slope erosion",
        "Blocked drainage",
        "Fill crack"
      ]
    },
    "dates": {
      "main": "Mar 20, 2026",
      "panes": [
        "Mar 14, 2025",
        "Jul 18, 2025",
        "Nov 21, 2025",
        "Mar 20, 2026"
      ]
    }
  }
} as const;

export default landingSite;
